<script>
  import { createEventDispatcher } from 'svelte';
  import { goto } from '$app/navigation';

  import AppInviteAjax from '@ajax/app_invite';
  import UserAjax from '@ajax/user';
  import Fullstory from '@utils/fullstory';
  import Log from '@utils/log';
  import Auth from '@utils/auth';

  import Modal from '@components/Modal.svelte';
  import ProgressBar from '@components/ProgressBar.svelte';

  import CreateOrLoginStep from './create_account/CreateOrLoginStep.svelte';
  import UsernameStep from './create_account/UsernameStep.svelte';
  import InviteCodeStep from './create_account/InviteCodeStep.svelte';
  import CongratsStep from './create_account/CongratsStep.svelte';
  import NoInviteCongratsStep from './create_account/NoInviteCongratsStep.svelte';
  import PhotoUploadStep from './create_account/PhotoUploadStep.svelte';
  import SocialStep from './create_account/SocialStep.svelte';
  import UserBioStep from './create_account/UserBioStep.svelte';
  import Track from '@utils/track';
  import AddBrandsStep from './create_account/AddBrandsStep.svelte';

  import { getCurrentUserStore } from '@stores/current_user';
  let logoSrc = '/images/landing_page/default/logo.svg';

  const dispatch = createEventDispatcher();

  export let showModal = false;
  export let mobile = false;
  export let overlayClass;
  export let allowClose = true;
  export let successCallback = function () {};
  export let afterLoginCallback = function () {};
  export let afterUserCreateCallback = function () {};
  export let createOrLoginEmailValidationFn = async (_emailValue) => {};
  export let saveButtonFlow = false;
  export let showInvitedUserBanner = true;
  export let showActionLink = true;
  export let fetchInviteCodeAutomaticallyUserId = null;
  export let inviteCode = null;

  export let prefilledName = null;
  export let prefilledEmail = null;
  export let prefilledInstagramHandle = null;
  export let prefilledTiktokHandle = null;
  export let prefilledBio = null;
  export let prefilledUsername = null;
  export let prefilledProfileImageUrl = null;
  export let eventDetails = null;

  let inviteUser = null;
  let currentUser = getCurrentUserStore('default');

  export let beforeDataSend = (data) => {
    return data;
  };

  let pendingUserData = {};
  let showUsernameStep = false;
  let showInviteCodeStep = false;
  let showCongratsStep = false;
  let showNoInviteCongratsStep = false;
  let showPhotoUploadStep = false;
  let showSocialStep = false;
  let showUserBioStep = false;
  let showAddBrandsStep = false;
  let createdUser;
  let progressValue = 0;

  $: showDefaultStep =
    !showUsernameStep &&
    !showInviteCodeStep &&
    !showCongratsStep &&
    !showNoInviteCongratsStep &&
    !showPhotoUploadStep &&
    !showSocialStep &&
    !showUserBioStep &&
    !showAddBrandsStep;

  $: showCloseButton = allowClose && !showPhotoUploadStep && !showUserBioStep && !showUsernameStep && !showSocialStep && !showUserBioStep && !showAddBrandsStep;

  let close = function () {
    if (showCongratsStep) {
      if (location.href && location.href.includes('/discover')) {
        location.reload();
      } else {
        goto(`/discover`);
      }
    } else {
      showModal = false;
      showInviteCodeStep = false;
      showCongratsStep = false;
      showPhotoUploadStep = false;
      showNoInviteCongratsStep = false;
      showSocialStep = false;
      showUserBioStep = false;
      showAddBrandsStep = false;
      createdUser = undefined;
      dispatch('close');
    }
  };

  let trackWaitlistMessageView = function () {
    Track.waitlistMessageView({
      pendingUserEmail: pendingUserData && pendingUserData.email,
      pageUrl: window.location.href,
    });
  };

  let goToInviteCodeStep = function () {
    showInviteCodeStep = true;
    showUsernameStep = false;
    showCongratsStep = false;
    showPhotoUploadStep = false;
    showNoInviteCongratsStep = false;
    showSocialStep = false;
    showUserBioStep = false;

    trackWaitlistMessageView();
  };

  let goToUsernameStep = function () {
    showCongratsStep = false;
    showInviteCodeStep = false;
    showUsernameStep = true;
    showPhotoUploadStep = false;
    showNoInviteCongratsStep = false;
    showSocialStep = false;
    showUserBioStep = false;
    progressValue = 30;
  };

  let goToNoInvitesCongratsStep = function () {
    showCongratsStep = false;
    showInviteCodeStep = false;
    showUsernameStep = false;
    showPhotoUploadStep = false;
    showNoInviteCongratsStep = true;
    showSocialStep = false;
    showUserBioStep = false;
  };

  let goToPhotoUploadStep = function () {
    showCongratsStep = false;
    showInviteCodeStep = false;
    showUsernameStep = false;
    showPhotoUploadStep = true;
    showNoInviteCongratsStep = false;
    showSocialStep = false;
    showUserBioStep = false;
    progressValue = 40;
  };

  let goToSocialStep = function () {
    showCongratsStep = false;
    showInviteCodeStep = false;
    showUsernameStep = false;
    showPhotoUploadStep = false;
    showNoInviteCongratsStep = false;
    showSocialStep = true;
    showUserBioStep = false;
    progressValue = 50;
  };

  let goToUserBioStep = function () {
    showCongratsStep = false;
    showInviteCodeStep = false;
    showUsernameStep = false;
    showPhotoUploadStep = false;
    showNoInviteCongratsStep = false;
    showSocialStep = false;
    showUserBioStep = true;
    progressValue = 60;
  };

  let goToAddBrandsStep = function () {
    showCongratsStep = false;
    showInviteCodeStep = false;
    showUsernameStep = false;
    showPhotoUploadStep = false;
    showNoInviteCongratsStep = false;
    showSocialStep = false;
    showUserBioStep = false;
    showAddBrandsStep = true;
    progressValue = 70;
  };

  let generatePossibleGroupIdeas = async function () {
    let resp = await UserAjax.generatePossibleGroupIdeas();

    if (resp && resp.processing) {
      currentUser.set({ ...currentUser, has_group_possible_ideas: resp.processing });
    }
  };

  let triggerMetaPixelTrackRegistration = () => {
    if (typeof window !== 'undefined' && window.fbq !== undefined) {
      window.fbq('track', 'CompleteRegistration');
    }
  };

  let convertPendingUserToActualUser = async function (userCodeVar, inviteCodeVar, displayName) {
    let resp = await AppInviteAjax.convertPendingUserToUser(userCodeVar, {
      invite_code: inviteCodeVar,
    });

    if (currentUser) {
      await Auth.signout(currentUser, { redirectToRoot: false });
    }

    Log.info(
      `[PENDING USER CONVERTED TO REAL USER] ${displayName} (${resp.invited_url}) used invite code by: ${resp.inviter_name} {${inviteCodeVar}} (${resp.inviter_url})`
    );

    if (!inviteUser) {
      inviteUser = { name: resp.inviter_name, avatar_url: resp.inviter_avatar_url };
    }

    let user = await Auth.load(currentUser, resp.token);

    triggerMetaPixelTrackRegistration();

    if (user && window.APP_ENV === 'production') {
      Fullstory.recordFS(user);
    }

    dispatch('user-created', { user });

    return user;
  };

  let fetchInviteUser = async function (inviteCodeToFetch) {
    let resp = await AppInviteAjax.get(inviteCodeToFetch);
    inviteUser = { name: resp.user.name, avatar_url: resp.user.avatar_url, id: resp.user.id };
  };

  let fetchInviteCodeAutomatically = async function () {
    let resp = await AppInviteAjax.skipInviteCode(fetchInviteCodeAutomaticallyUserId);

    if (resp && resp.code && !inviteCode) {
      inviteCode = resp.code;
      fetchInviteUser(inviteCode);
    }
  };

  $: getContainerClass = function () {
    if (showInviteCodeStep || showNoInviteCongratsStep) {
      return mobile ? 'mobile-invite-flow-modal__container' : 'desktop-invite-flow-modal__container';
    } else if (showUsernameStep || showSocialStep || showPhotoUploadStep || showUserBioStep || showAddBrandsStep || showCongratsStep) {
      return mobile ? 'mobile-modal__container mobile-invite-flow-modal__container--full' : 'desktop-invite-flow-modal__container desktop-invite-flow-modal__container--full';
    } else {
      return mobile ? 'mobile-modal__container' : 'desktop-modal__container';
    }
  };

  if (inviteCode) {
    fetchInviteUser(inviteCode);
  }

  if (eventDetails) {
    Track.signupModalOpened({ ...eventDetails, page_url: location.pathname + location.search });
  }

  if (fetchInviteCodeAutomaticallyUserId && !inviteCode) {
    fetchInviteCodeAutomatically();
  }
</script>

<style style="text/scss" lang="scss">
  @import '../../vendor/design-system.scss';
  
  * :global(.desktop-modal__container) {
    border-radius: 16px !important;
    overflow: hidden !important;
    margin-top: 0px !important;
    margin-bottom: 0px !important;
    max-height: 860px !important;
    max-width: 440px !important;
    padding: 0px !important;
  }

  * :global(.mobile-modal__container .modal__content),
  * :global(.desktop-modal__container .modal__content),
  * :global(.desktop-invite-flow-modal__container .modal__content),
  * :global(.mobile-invite-flow-modal__container .modal__content) {
    margin: 0px !important;
  }

  * :global(.desktop-invite-flow-modal__container) {
    border-radius: 16px !important;
    width: 100% !important;
    overflow: hidden !important;
    margin-top: 0px !important;
    margin-bottom: 0px !important;
    padding: 0px !important;
    max-width: 440px !important;
  }

  * :global(.desktop-invite-flow-modal__container--full) {
    border-radius: 0 !important;
    max-width: 100% !important;
    min-height: 100vh !important;
  }

  * :global(.desktop-invite-flow-modal__container.congrats-step) {
    height: 684px !important;
    max-height: 684px !important;
    background: linear-gradient(343.65deg, #eae4ff -0.74%, #ffffff 72.21%),
      linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
  }

  * :global(.mobile-invite-flow-modal__container.congrats-step) {
    background: linear-gradient(343.65deg, #eae4ff -0.74%, #ffffff 72.21%),
      linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
  }

  * :global(.mobile-invite-flow-modal__container) {
    overflow-x: hidden !important;
    min-height: unset !important;
    height: unset !important;
    top: 70px !important;
    bottom: 10px !important;
    left: 10px !important;
    right: 10px !important;
  }

  * :global(.modal__close) {
    position: absolute;
    right: 20px;
    top: 15px;
  }

  .svelte-create-login-modal {
    .progress-bar-container {
      transform: translateY(-16px);
      margin-bottom: -18px;
      line-height: 1;
    }
  }

  .invite-flow-header {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    width: 100%;
    height: 114px;
  }

  .mobile {
    .invite-flow-header {
      height: 60px;
    }
  }

  .step-container {
    &.hide-step {
      opacity: 0;
      max-height: 0;
      overflow: hidden;
      position: relative;
      z-index: -1;
    }
  }

  .progress-bar-wrapper {
    height: 4px;
    margin-bottom: -4px;
  }
</style>

<div class="svelte-modal svelte-create-login-modal" class:mobile={mobile}>
  <Modal
    mobile={mobile}
    on:close={close}
    show={showModal}
    fadeInDuration={0}
    allowClose={allowClose}
    overlayClass={overlayClass}
    closeOnOverlayClick={false}
    containerClass={getContainerClass()}
    showCloseButton={showCloseButton}
    style={getContainerClass().includes('mobile-invite-flow-modal__container--full')
      ? 'border-radius: 0 !important; position: absolute !important; margin: 0px !important; padding: 0px !important; width: 100% !important; height: 100% !important;'
      : ''}>
    <div slot="content" style="text-align: center;">
      <div class="invite-flow-header" class:hidden={!showUsernameStep && !showPhotoUploadStep && !showSocialStep && !showUserBioStep && !showAddBrandsStep}>
        <img
          alt="landing page"
          loading="lazy"
          src={logoSrc}
          class=""
        />
      </div>
      
      {#if showUsernameStep || showPhotoUploadStep || showSocialStep || showUserBioStep || showAddBrandsStep}
        <div class="progress-bar-wrapper">
          <ProgressBar value={progressValue} />
        </div>
      {/if}

      <div class="step-container" class:hide-step={!showInviteCodeStep}>
        <InviteCodeStep
          mobile={mobile}
          successCallback={successCallback}
          pendingUserData={pendingUserData}
          convertPendingUserToActualUser={convertPendingUserToActualUser}
          closeModal={close}
          on:finished={(event) => {
            if (event.detail.inviteCode && !inviteCode) {
              inviteCode = event.detail.inviteCode;
            }

            if (event.detail.user) {
              createdUser = event.detail.user;
              goToUsernameStep();
            } else {
              close();
            }
          }}
          on:go-to-no-invites-contrats-step={goToNoInvitesCongratsStep}
        />
      </div>

      <div class="step-container" class:hide-step={!showUsernameStep}>
        <UsernameStep
          showInvitedUserBanner={showInvitedUserBanner}
          mobile={mobile}
          inviteUser={inviteUser}
          successCallback={successCallback}
          saveButtonFlow={saveButtonFlow}
          prefilledUsername={prefilledUsername}
          closeModal={close}
          on:finished={(event) => {
            createdUser = event.detail.user;
            if (saveButtonFlow) {
              successCallback(createdUser);
            } else {
              goToPhotoUploadStep();
            }
          }}
        />
      </div>

      <div class="step-container" class:hide-step={!showCongratsStep}>
        <CongratsStep
          closeModal={close}
          mobile={mobile}
          pendingUserData={pendingUserData}
          createdUser={createdUser}
          on:finished={(_event) => {
            successCallback(createdUser);
            close();
          }}
        />
      </div>

      <div class="step-container" class:hide-step={!showNoInviteCongratsStep}>
        <NoInviteCongratsStep
          mobile={mobile}
          pendingUserData={pendingUserData}
        />
      </div>

      <div class="step-container" class:hide-step={!showPhotoUploadStep}>
        <PhotoUploadStep
          mobile={mobile}
          createdUser={createdUser}
          inviteCode={inviteCode}
          prefilledProfileImageUrl={prefilledProfileImageUrl}
          on:finished={() => {
            goToSocialStep();
          }}
          on:back={() => {
            goToUsernameStep();
          }}
        />
      </div>

      <div class="step-container" class:hide-step={!showSocialStep}>
        <SocialStep
          mobile={mobile}
          createdUser={createdUser}
          prefilledInstagramHandle={prefilledInstagramHandle}
          prefilledTiktokHandle={prefilledTiktokHandle}
          isActive={showSocialStep}
          on:skip={() => {
            goToUserBioStep();
          }}
          on:back={() => {
            goToPhotoUploadStep();
          }}
          on:finished={() => {
            goToUserBioStep();
          }}
        />
      </div>

      <div class="step-container" class:hide-step={!showUserBioStep}>
        <UserBioStep
          mobile={mobile}
          createdUser={createdUser}
          prefilledBio={prefilledBio}
          on:finished={() => {
            goToAddBrandsStep();
          }}
          on:back={() => {
            goToSocialStep();
          }}
        />
      </div>

      <div class="step-container" class:hide-step={!showAddBrandsStep}>
        <AddBrandsStep
          mobile={mobile}
          createdUser={createdUser}
          showAddBrandsModal={showAddBrandsStep}
          on:finished={() => {
            setTimeout(generatePossibleGroupIdeas, 1000);
            successCallback(createdUser);
            close();
          }}
        />
      </div>

      <div class="step-container" class:hide-step={!showDefaultStep}>
        <CreateOrLoginStep
          {...$$props}
          closeModal={close}
          mobile={mobile}
          successCallback={successCallback}
          afterLoginCallback={afterLoginCallback}
          beforeDataSend={beforeDataSend}
          saveButtonFlow={saveButtonFlow}
          emailValidationFn={createOrLoginEmailValidationFn}
          showActionLink={showActionLink}
          prefilledName={prefilledName}
          prefilledEmail={prefilledEmail}
          on:go-to-username-step={(event) => {
            pendingUserData = event.detail;

            if (inviteCode) {
              convertPendingUserToActualUser(pendingUserData.code, inviteCode, pendingUserData.name).then((user) => {
                createdUser = user;
                afterUserCreateCallback(createdUser);
                goToUsernameStep();
              });
            } else if (saveButtonFlow) {
              goToUsernameStep();
            } else {
              goToInviteCodeStep();
            }
          }}
        />
      </div>
    </div>
  </Modal>
</div>
